import { formatHyphenatedDate, getCurrentJstDayjs } from '@/utils/date';
import type { WabunkaReservationFormStep1 } from '@/wabunka-src/features/Experience';

import type dayjs from 'dayjs';

const NEXT_DAY = 1;
const PREVIOUS_DAY = -1;

interface UseDateSelectionProps {
  reservationStep1Form: WabunkaReservationFormStep1;
  onUpdateSelectedDateAndExpEventId: (selectedDate: dayjs.Dayjs | null, experienceEventId: string) => void;
}

/**
 * 日付選択肢管理用Composable
 */
export const useDateSelection = ({ reservationStep1Form, onUpdateSelectedDateAndExpEventId }: UseDateSelectionProps) => {
  const initialDate = formatHyphenatedDate(reservationStep1Form.selectedDate?.startOf('month') ?? getCurrentJstDayjs().startOf('month'));
  const startDateFrom = ref(initialDate);
  const startDateTo = ref(initialDate);

  const onChangeMonth = (startDate: string, endDate: string) => {
    startDateFrom.value = startDate;
    startDateTo.value = endDate;
  };

  const onClickDate = (date: dayjs.Dayjs) => {
    onUpdateSelectedDateAndExpEventId(date, '');
  };

  /**
   * 指定された日数だけ選択日を変更する関数
   * @param daysDelta - 日付をシフトする日数。正の値は未来へ、負の値は過去へ移動
   */
  const changeSelectedDate = (daysDelta: number) => {
    const operation = daysDelta >= 0 ? 'add' : 'subtract';
    const daysDeltaAbs = Math.abs(daysDelta);

    const newSelectedDate = reservationStep1Form.selectedDate?.[operation](daysDeltaAbs, 'day') ?? null;

    if (reservationStep1Form.selectedDate !== newSelectedDate) {
      onUpdateSelectedDateAndExpEventId(newSelectedDate, '');
    }

    if (newSelectedDate == null) {
      return;
    }

    // refも更新
    startDateFrom.value = formatHyphenatedDate(newSelectedDate);
    startDateTo.value = formatHyphenatedDate(newSelectedDate);
  };

  const onClickNextButton = () => {
    changeSelectedDate(NEXT_DAY);
  };

  const onClickPreviousButton = () => {
    changeSelectedDate(PREVIOUS_DAY);
  };

  // 選択された日付が変更された時の監視
  watch(
    () => reservationStep1Form.selectedDate,
    (newDate) => {
      if (newDate) {
        // APIパラメータである日付を更新することでfetchが走るようにする
        startDateFrom.value = formatHyphenatedDate(newDate);
        startDateTo.value = formatHyphenatedDate(newDate);
      }
    },
    { immediate: true },
  );

  return {
    startDateFrom,
    startDateTo,
    onChangeMonth,
    onClickDate,
    onClickNextButton,
    onClickPreviousButton,
  };
};
